// /referral — Public physician / clinician referral form.
// Doctors, PCPs, social workers, school counselors send patients to
// Let Go Wellness through this page. Submissions live in MongoDB and
// appear in the admin dashboard under "Referrals".

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import { toast } from "sonner";
import { ArrowLeft, Check, Loader2, Download, Stethoscope } from "lucide-react";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSite } from "../context/SiteContext";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Individual therapy",
  "Couples therapy",
  "Family therapy",
  "Group therapy",
  "Trauma / EMDR",
  "Psychiatric evaluation",
  "Medication management",
  "Assessment / testing",
  "Other",
];

const URGENCY_OPTIONS = [
  { value: "routine", label: "Routine", hint: "Within 2-3 weeks" },
  { value: "expedited", label: "Expedited", hint: "Within 5-7 business days" },
  { value: "urgent", label: "Urgent", hint: "Within 48 hours" },
];

export default function ReferralForm() {
  const { site } = useSite();
  const brand = site?.brand_name || "Let Go Wellness";
  const navigate = useNavigate();

  const [form, setForm] = useState({
    provider_name: "",
    provider_credentials: "",
    provider_practice: "",
    provider_npi: "",
    provider_phone: "",
    provider_fax: "",
    provider_email: "",
    provider_address: "",
    patient_name: "",
    patient_dob: "",
    patient_phone: "",
    patient_email: "",
    patient_insurance: "",
    reason: "",
    diagnosis: "",
    symptoms: "",
    requested_service: SERVICE_OPTIONS[0],
    urgency: "routine",
    clinical_history: "",
    current_medications: "",
    prior_treatment: "",
    signature: "",
    roi_consent: false,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const update = (k) => (e) =>
    setForm((prev) => ({
      ...prev,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.roi_consent) {
      toast.error("Please confirm the patient has authorized release of information.");
      return;
    }
    setBusy(true);
    const t = toast.loading("Submitting the referral…");
    try {
      const res = await axios.post(`${API_BASE}/referrals/submit`, form);
      setDone(res.data);
      toast.success("Referral received — we'll be in touch within 1 business day.", { id: t });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || err.message || "Something went wrong.";
      toast.error(msg, { id: t });
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = () => {
    if (!done) return;
    const pdf = new jsPDF({ unit: "in", format: "letter" });
    const W = 8.5;
    const M = 0.7;
    let y = 0.8;

    pdf.setFont("times", "bold");
    pdf.setFontSize(20);
    pdf.text(brand, M, y);
    y += 0.3;
    pdf.setFont("times", "italic");
    pdf.setFontSize(11);
    pdf.setTextColor(120, 115, 105);
    pdf.text("Provider Referral Form", M, y);
    pdf.setTextColor(28, 25, 23);
    y += 0.35;
    pdf.setDrawColor(180, 170, 150);
    pdf.setLineWidth(0.005);
    pdf.line(M, y, W - M, y);
    y += 0.25;

    const section = (title) => {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(159, 92, 69);
      pdf.text(title.toUpperCase(), M, y);
      pdf.setTextColor(28, 25, 23);
      y += 0.22;
    };
    const row = (label, value) => {
      if (!value) return;
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.text(label, M, y);
      pdf.setFont("helvetica", "normal");
      const lines = pdf.splitTextToSize(value || "—", W - 2 * M - 1.8);
      pdf.text(lines, M + 1.8, y);
      y += 0.2 * Math.max(1, lines.length);
    };

    section("Referring provider");
    row("Name", `${done.provider_name}${done.provider_credentials ? ", " + done.provider_credentials : ""}`);
    row("Practice", done.provider_practice);
    row("NPI", done.provider_npi);
    row("Phone", done.provider_phone);
    row("Fax", done.provider_fax);
    row("Email", done.provider_email);
    row("Address", done.provider_address);
    y += 0.15;

    section("Patient");
    row("Name", done.patient_name);
    row("DOB", done.patient_dob);
    row("Phone", done.patient_phone);
    row("Email", done.patient_email);
    row("Insurance", done.patient_insurance);
    y += 0.15;

    if (y > 9.5) { pdf.addPage(); y = 0.8; }
    section("Clinical");
    row("Reason", done.reason);
    row("Diagnosis", done.diagnosis);
    row("Symptoms", done.symptoms);
    row("Service requested", done.requested_service);
    row("Urgency", done.urgency);
    row("History", done.clinical_history);
    row("Medications", done.current_medications);
    row("Prior treatment", done.prior_treatment);
    y += 0.25;

    if (y > 9.5) { pdf.addPage(); y = 0.8; }
    pdf.line(M, y, W - M, y);
    y += 0.3;
    section("Signed");
    pdf.setFont("times", "italic");
    pdf.setFontSize(14);
    pdf.text(done.signature, M, y);
    y += 0.2;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(120, 115, 105);
    pdf.text(`ROI consent confirmed · ${new Date(done.signed_at).toLocaleString()}`, M, y);

    pdf.save(`referral-${(done.patient_name || "patient").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`);
  };

  if (done) {
    return (
      <main className="min-h-screen bg-[#FDFBF7] text-[#1C1917] font-sans antialiased" data-testid="referral-success">
        <Navbar />
        <section className="pt-48 pb-24">
          <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#3A5A40]/10 flex items-center justify-center mb-6">
              <Check className="w-8 h-8 text-[#3A5A40]" />
            </div>
            <p className="overline text-[#9F5C45] mb-3">Referral received</p>
            <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight mb-4">
              Thank you, {done.provider_name.split(" ").slice(-1)[0]}.
            </h1>
            <p className="text-[#57534E] max-w-xl mx-auto mb-8">
              We&apos;ve received your referral for <strong>{done.patient_name}</strong>. Our care
              coordinator will reach out within 1 business day and confirm receipt with you directly.
              Please download a copy for your records.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={downloadPdf}
                data-testid="referral-download-pdf"
                className="inline-flex items-center gap-2 px-6 py-3 btn-primary text-xs uppercase tracking-[0.22em]"
              >
                <Download className="w-4 h-4" /> Download referral PDF
              </button>
              <button
                onClick={() => navigate("/")}
                data-testid="referral-back-home"
                className="inline-flex items-center gap-2 px-6 py-3 btn-ghost text-xs uppercase tracking-[0.22em]"
              >
                Back to site
              </button>
            </div>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#FDFBF7] text-[#1C1917] font-sans antialiased" data-testid="referral-page">
      <Navbar />
      <section className="pt-48 pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#9F5C45] hover:text-[#3A5A40]"
            data-testid="referral-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
          <p className="overline text-[#9F5C45] mt-6 mb-3 flex items-center gap-2">
            <Stethoscope className="w-3.5 h-3.5" /> Provider referral
          </p>
          <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-[#1C1917] mb-4">
            Refer a patient to <em className="text-[#3A5A40]">Let Go.</em>
          </h1>
          <p className="text-[#57534E] max-w-2xl">
            For physicians, PCPs, social workers, school counselors, and licensed clinicians.
            Our care coordinator responds to every referral within one business day. Urgent referrals
            are triaged same-day.
          </p>

          <form onSubmit={submit} className="mt-12 space-y-12" data-testid="referral-form">
            {/* Referring provider */}
            <fieldset className="grid md:grid-cols-2 gap-6">
              <legend className="overline text-[#9F5C45] mb-4 block col-span-full">Referring provider</legend>
              <Input label="Your full name" required value={form.provider_name} onChange={update("provider_name")} testId="provider_name" />
              <Input label="Credentials" placeholder="MD, DO, APRN, LCSW…" value={form.provider_credentials} onChange={update("provider_credentials")} testId="provider_credentials" />
              <Input label="Practice / organization" className="md:col-span-2" value={form.provider_practice} onChange={update("provider_practice")} testId="provider_practice" />
              <Input label="NPI (optional)" value={form.provider_npi} onChange={update("provider_npi")} testId="provider_npi" />
              <Input label="Practice address" value={form.provider_address} onChange={update("provider_address")} testId="provider_address" />
              <Input label="Phone" type="tel" required value={form.provider_phone} onChange={update("provider_phone")} testId="provider_phone" />
              <Input label="Fax" value={form.provider_fax} onChange={update("provider_fax")} testId="provider_fax" />
              <Input label="Email" type="email" required className="md:col-span-2" value={form.provider_email} onChange={update("provider_email")} testId="provider_email" />
            </fieldset>

            {/* Patient */}
            <fieldset className="grid md:grid-cols-2 gap-6">
              <legend className="overline text-[#9F5C45] mb-4 block col-span-full">Patient</legend>
              <Input label="Patient full name" required value={form.patient_name} onChange={update("patient_name")} testId="patient_name" />
              <Input label="Date of birth" placeholder="MM / DD / YYYY" required value={form.patient_dob} onChange={update("patient_dob")} testId="patient_dob" />
              <Input label="Phone" type="tel" value={form.patient_phone} onChange={update("patient_phone")} testId="patient_phone" />
              <Input label="Email" type="email" value={form.patient_email} onChange={update("patient_email")} testId="patient_email" />
              <Input label="Insurance" className="md:col-span-2" placeholder="Provider + member ID (if known)" value={form.patient_insurance} onChange={update("patient_insurance")} testId="patient_insurance" />
            </fieldset>

            {/* Clinical */}
            <fieldset className="space-y-6">
              <legend className="overline text-[#9F5C45] mb-4 block">Clinical information</legend>
              <Textarea label="Reason for referral" required rows={3} value={form.reason} onChange={update("reason")} testId="reason" placeholder="Why are you referring this patient? What are you hoping we can help with?" />
              <div className="grid md:grid-cols-2 gap-6">
                <Input label="Diagnosis / ICD-10 code(s)" value={form.diagnosis} onChange={update("diagnosis")} testId="diagnosis" />
                <div>
                  <span className="overline text-[#57534E] mb-2 block">Service requested</span>
                  <select
                    value={form.requested_service}
                    onChange={update("requested_service")}
                    data-testid="referral-input-requested_service"
                    className="w-full border-0 border-b-2 border-[#D6CFC2] bg-transparent px-1 py-3 text-base focus:outline-none focus:border-[#3A5A40]"
                  >
                    {SERVICE_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
              <Textarea label="Current symptoms" rows={2} value={form.symptoms} onChange={update("symptoms")} testId="symptoms" />

              {/* Urgency picker */}
              <div>
                <span className="overline text-[#57534E] mb-3 block">Urgency</span>
                <div className="grid md:grid-cols-3 gap-3">
                  {URGENCY_OPTIONS.map((u) => (
                    <button
                      key={u.value}
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, urgency: u.value }))}
                      data-testid={`urgency-${u.value}`}
                      className={`text-left p-4 border transition-colors ${
                        form.urgency === u.value
                          ? "border-[#3A5A40] bg-[#3A5A40]/5"
                          : "border-[#D6CFC2] hover:border-[#3A5A40]"
                      }`}
                    >
                      <p className="font-serif text-base text-[#1C1917]">{u.label}</p>
                      <p className="text-xs text-[#86827B] mt-0.5">{u.hint}</p>
                    </button>
                  ))}
                </div>
              </div>

              <Textarea label="Relevant clinical history" rows={3} value={form.clinical_history} onChange={update("clinical_history")} testId="clinical_history" />
              <Textarea label="Current medications" rows={2} value={form.current_medications} onChange={update("current_medications")} testId="current_medications" />
              <Textarea label="Prior mental-health treatment" rows={2} value={form.prior_treatment} onChange={update("prior_treatment")} testId="prior_treatment" />
            </fieldset>

            {/* ROI + signature */}
            <fieldset className="space-y-6 border-t border-[#E2DCD0] pt-8">
              <legend className="overline text-[#9F5C45] mb-4 block">Release of information & sign-off</legend>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.roi_consent}
                  onChange={update("roi_consent")}
                  data-testid="roi-consent"
                  className="mt-1 w-4 h-4 accent-[#3A5A40]"
                />
                <span className="text-sm text-[#1C1917]">
                  I confirm that the patient has authorized release of information between my practice and {brand} for the
                  purpose of coordinating care. <span className="text-[#9F5C45]">Required.</span>
                </span>
              </label>
              <Input
                label="Your electronic signature (type full name)"
                required
                value={form.signature}
                onChange={update("signature")}
                testId="signature"
                fontStyle="italic"
              />
            </fieldset>

            <div className="border-t border-[#E2DCD0] pt-6">
              <button
                type="submit"
                disabled={busy}
                data-testid="referral-submit"
                className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 btn-primary text-xs uppercase tracking-[0.22em] disabled:opacity-60"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <Stethoscope className="w-4 h-4" /> Send referral
                  </>
                )}
              </button>
              <p className="text-xs text-[#86827B] mt-3">
                For urgent psychiatric emergencies please call <strong>988</strong> or direct the patient to their nearest emergency room.
              </p>
            </div>
          </form>
        </div>
      </section>
      <Footer />
    </main>
  );
}

function Input({ label, className = "", required, testId, fontStyle, ...rest }) {
  return (
    <label className={`block ${className}`}>
      <span className="overline text-[#57534E] mb-2 block">
        {label} {required && <span className="text-[#9F5C45]">*</span>}
      </span>
      <input
        required={required}
        data-testid={`referral-input-${testId}`}
        style={fontStyle ? { fontStyle } : undefined}
        className="w-full border-0 border-b-2 border-[#D6CFC2] bg-transparent px-1 py-3 text-base focus:outline-none focus:border-[#3A5A40] transition-colors"
        {...rest}
      />
    </label>
  );
}

function Textarea({ label, required, testId, rows = 3, ...rest }) {
  return (
    <label className="block">
      <span className="overline text-[#57534E] mb-2 block">
        {label} {required && <span className="text-[#9F5C45]">*</span>}
      </span>
      <textarea
        required={required}
        rows={rows}
        data-testid={`referral-input-${testId}`}
        className="w-full border border-[#D6CFC2] bg-white px-3 py-2 text-base focus:outline-none focus:border-[#3A5A40] resize-none"
        {...rest}
      />
    </label>
  );
}
